import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { MemberToolbar } from "CrossFeatureComponents/MemberToolbar/MemberToolbar";
import API from "Signups/API.js";
import { useSignups } from "Signups/state.js";
import {
  useBackgroundColor,
  useHasDescriptionOrMedia,
} from "Signups/stateHooks";
import { motion } from "framer-motion";

import FnrLoadingCarousel from "UI/FnrLoadingCarousel";

import SignupClosed from "../SharedComponents/SignupClosed";

import Confetti from "./Confetti";
import InfoColumn from "./InfoColumn";
import SignupHeader from "./SignupHeader";
import { useHandleAuthSuccess } from "./SignupItem/stateHooks";
import SignupItemsList from "./SignupItemsList";
import SignupNotFound from "./SignupNotFound";
import SignupPayment from "./SignupPayment";
import useShowPayNowArea from "./SignupPayment/useShowPayNowArea";
import { Container } from "./Styles";
import { mockData } from "./mockData/mockData";

SignupsPublicPage.propTypes = {
  match: PropTypes.object, // from react-router
};

export default function SignupsPublicPage({ match }) {
  const headerRef = useRef();
  const { signupListID, fundID } = match?.params;
  const [state, dispatch] = useSignups();
  const [fetchStatus, setFetchStatus] = useState(null);
  const [payNowHeight, setPayNowHeight] = useState();
  const { handleAuthSuccess } = useHandleAuthSuccess();

  const { paymentAmountNeeded, expandedItemID } = state;
  const signupIsArchived = state.currentSignupList?.signupListStatusID === 4;

  const { hasTwoColumns } = useHasDescriptionOrMedia();

  const footerRef = useCallback((node) => {
    if (node !== null) {
      setPayNowHeight(node.getBoundingClientRect().height);
      return;
    }
    setPayNowHeight(0);
  }, []);

  useEffect(() => {
    dispatch({ type: "Remove Current Signup List" });
    if (!signupListID || parseInt(signupListID) === 0) return;
    const getSignupListInfo = API.getSignupListInfo(signupListID);
    setFetchStatus("fetching");

    getSignupListInfo
      .post()
      .then((signupListInfo) => {
        // console.log("signupListInfo", signupListInfo);
        const hasExistingUnpaidItems = getHasExistingUnpaidItems(
          signupListInfo?.userSignupItems
        );

        if (mockData?.shouldMock) {
          console.log("mocking");
          dispatch({
            type: "Set Current Signup List",
            hasExistingUnpaidItems,
            ...mockData.mockViewResponse,
          });
        } else {
          dispatch({
            type: "Set Current Signup List",
            hasExistingUnpaidItems,
            ...signupListInfo,
          });
        }

        setFetchStatus("success");
      })
      .catch((err) => {
        console.error(err);
        setFetchStatus("failed");
      });
    return () => getSignupListInfo.cancel();
  }, [signupListID, dispatch]);

  useEffect(() => {
    const title = state?.currentSignupList?.title || "Signup";
    document.title = `${title} | Flocknote`;
  }, [state.currentSignupList]);

  const signupHasCostAssociated = state.currentSignupList.fundIt?.hasSignupCost;

  const { gradientString, backgroundColor: toolbarAccentColor } =
    useBackgroundColor();

  const backgroundColor = fetchStatus === "fetching" ? "white" : gradientString;

  const isShowingPaymentUI =
    Number(paymentAmountNeeded) > 0 && !expandedItemID && !signupIsArchived;

  const headerHeight = headerRef.current?.getBoundingClientRect()?.height;
  const { showPayNowArea } = useShowPayNowArea();

  return (
    <>
      <OuterWrapper
        className="FnrSinglePageApp"
        data-cy="SignupsPublicPage_Container"
        $isShowingPaymentUI={isShowingPaymentUI}
      >
        <Wrapper className="SignupApp">
          {fetchStatus === "fetching" ? (
            <FnrLoadingCarousel show={true} whiteBackground />
          ) : null}
          {fetchStatus === "failed" ? <SignupNotFound /> : null}
          {fetchStatus === "success" ? (
            <>
              <Confetti />

              <SignupHeader headerRef={headerRef} />

              <ContentGrid $hasTwoColumns={hasTwoColumns}>
                <Main
                  $hasTwoColumns={hasTwoColumns}
                  $isShowingPaymentUI={isShowingPaymentUI}
                  $headerHeight={headerHeight}
                  $payNowHeight={payNowHeight}
                >
                  <ContainerStyled payNowHeight={payNowHeight}>
                    <SignupClosed />
                    <SignupItemsList isShowingPaymentUI={isShowingPaymentUI} />
                  </ContainerStyled>
                </Main>

                <InfoColumn />

                {/* Needed a separate background element to animate it in */}
                <Background
                  style={{ background: backgroundColor }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                />
              </ContentGrid>

              {signupHasCostAssociated ? (
                <SignupPayment footerRef={footerRef} />
              ) : null}
            </>
          ) : null}
        </Wrapper>
      </OuterWrapper>
      <MemberToolbar
        contextInquiryText="I have a question about this signup"
        loginVariant="signupList"
        onAuthChange={handleAuthSuccess}
        bottomOffset={showPayNowArea && !fundID ? 100 : 0}
        accentColor={toolbarAccentColor}
      />
    </>
  );
}

const OuterWrapper = styled.div`
  position: relative;
  background-color: #f1f1f1;
  padding-bottom: ${({ $isShowingPaymentUI }) =>
    $isShowingPaymentUI ? "200px" : "0"};
`;

const Background = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  /* max-height: 100vh; */
  min-height: 100vh;
  padding-bottom: ${({ $bigBottomPadding }) =>
    $bigBottomPadding ? "185px" : "0"};
  --accentColor: #278dc1;
`;

const ContentGrid = styled.div`
  display: grid;
  gap: 4rem;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  padding: 0 1rem;
  box-sizing: border-box;
  z-index: 1;

  @media screen and (min-width: 1000px) {
    max-width: ${({ $hasTwoColumns }) => ($hasTwoColumns ? "1024px" : "600px")};
    grid-template-columns: ${({ $hasTwoColumns }) =>
      $hasTwoColumns ? "1fr 33% " : "1fr"};
  }
`;

const Main = styled.main`
  position: relative;
  display: flex;
  justify-content: center;
  margin-bottom: 30px;

  /* @media screen and (min-width: 1000px) {
    ${({
    $hasTwoColumns,
    $isShowingPaymentUI,
    $headerHeight = 0,
    $payNowHeight,
  }) => {
    console.log({ $headerHeight });
    return $hasTwoColumns
      ? `
          max-height: calc(100vh - ${$headerHeight}px - ${
          $isShowingPaymentUI ? `${$payNowHeight}px` : "0px"
        });
          scrollbar-width: thin;
          scrollbar-color: hsl(0 0% 100% / 0.25) transparent;
          overflow-y: auto;
      `
      : "";
  }}
  } */
  margin-bottom: 54px; /* Leaving room for MemberToolbar */
`;

const ContainerStyled = styled(Container)`
  margin: 0;
  padding: 0;
`;

const getHasExistingUnpaidItems = (userSignupItems) => {
  if (!userSignupItems) return false;

  const respondents = Object.entries(userSignupItems)
    .map((entry) => entry[1].respondents)
    .reduce((acc, cur) => {
      return [...acc, ...cur];
    }, []);

  const hasUnpaid = respondents.some(
    (respondent) => respondent.paymentStatus === "Unpaid"
  );

  return hasUnpaid;
};
